import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import {
  Building2,
  User,
  Mail,
  Lock,
  Briefcase,
  CheckCircle2,
  ArrowLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from "../components/LanguageSwitcher";

function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    industry: "Teknoloji",
    name: "",
    email: "",
    phone: "",
    password: ""
  });

  const industries = [
    { key: "industryTechnology", value: "Teknoloji" },
    { key: "industrySoftware", value: "Yazılım" },
    { key: "industryEcommerce", value: "E-Ticaret" },
    { key: "industryConstruction", value: "İnşaat" },
    { key: "industryManufacturing", value: "Üretim" },
    { key: "industryHealthcare", value: "Sağlık" },
    { key: "industryEducation", value: "Eğitim" },
    { key: "industryFinance", value: "Finans" },
    { key: "industryConsulting", value: "Danışmanlık" },
    { key: "industryMedia", value: "Medya" },
    { key: "industryTourism", value: "Turizm" },
    { key: "industryLogistics", value: "Lojistik" },
    { key: "industryRetail", value: "Perakende" },
    { key: "industryOther", value: "Diğer" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.company_name || !formData.name || !formData.email || !formData.phone || !formData.password) {
      toast.error(t('fillAllFields'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalıdır");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post("/auth/register", formData);
      
      // Save token
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      toast.success(t('registerSuccess'));
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
      
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || t('registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 to-blue-100/50" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
      <div className="absolute top-40 right-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
      
      {/* Back Button Only - No Language Switcher */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">{t('backToHome')}</span>
          <span className="sm:hidden">{t('back')}</span>
        </Button>
      </div>

      <div className="relative w-full max-w-5xl">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left Side - Info */}
          <div className="hidden md:block">
            
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg p-3">
                <CheckCircle2 className="h-8 w-8 text-white" />
              </div>
              <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Milestora
              </span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Hemen Başlayın
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Ücretsiz hesabınızı oluşturun ve proje yönetiminde yeni bir çağa adım atın.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-full p-2 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('startInstantly')}</h3>
                  <p className="text-gray-600">{t('startInstantlyDesc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-full p-2 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('free14Days')}</h3>
                  <p className="text-gray-600">{t('free14DaysDesc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-full p-2 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('fullFeatures')}</h3>
                  <p className="text-gray-600">{t('fullFeaturesDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="border-2 border-gray-200 shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl">{t('register')}</CardTitle>
              <CardDescription>
                {t('registerDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Company Information */}
                <div className="space-y-4 pb-4 border-b">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {t('companyInfo')}
                  </h3>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('companyName')} *
                    </label>
                    <Input
                      type="text"
                      placeholder={t('companyNamePlaceholder')}
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('industry')}
                    </label>
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => setFormData({ ...formData, industry: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectIndustry')} />
                      </SelectTrigger>
                      <SelectContent>
                        {industries.map((industry) => (
                          <SelectItem key={industry.value} value={industry.value}>
                            {t(industry.key)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* User Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {t('adminUserInfo')}
                  </h3>

                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('nameSurname')} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder={t('namePlaceholder')}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="ahmet@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('phoneNumber')} *
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="tel"
                        placeholder={t('phonePlaceholder')}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('phoneHelpText')}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t('password')} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="password"
                        placeholder="En az 6 karakter"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10"
                        required
                        minLength={6}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('passwordHelpText')}
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  disabled={loading}
                >
                  {loading ? t('registering') : t('createCompanyButton')}
                </Button>

                <div className="text-center text-sm text-gray-600">
                  {t('alreadyHaveAccount')}{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-purple-600 hover:text-purple-700 font-semibold"
                  >
                    {t('loginLink')}
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Register;
